import { useState } from "react";
import Model from "react-body-highlighter";
import {
  Trophy,
  Heart,
  MessageCircle,
  Camera,
  Lock,
  FileText,
  TrendingUp,
  Check,
  Calendar,
  Video,
} from "lucide-react";
import { FadeUp } from "./FadeUp";

const MockCard = ({ children, className = "" }) => (
  <div className={`bg-[#141416] border border-[#232326] rounded-[28px] p-5 sm:p-6 shadow-2xl ${className}`}>
    {children}
  </div>
);

const Tag = ({ children }) => (
  <span className="inline-block bg-[#C6FF3D]/10 border border-[#C6FF3D]/30 text-[#C6FF3D] text-xs font-semibold tracking-wide px-3 py-1 rounded-full mb-4">
    {children}
  </span>
);

const CONFETTI = [
  { top: "8%", left: "12%", rot: 12, color: "#C6FF3D" },
  { top: "15%", left: "85%", rot: -20, color: "#ffffff" },
  { top: "70%", left: "8%", rot: 30, color: "#A1A1AA" },
  { top: "80%", left: "90%", rot: -10, color: "#C6FF3D" },
  { top: "10%", left: "50%", rot: 45, color: "#ffffff" },
];

const PRCommunityMock = () => (
  <MockCard>
    <div className="relative bg-[#0A0A0B] border border-[#C6FF3D]/40 rounded-2xl p-5 mb-5 overflow-hidden">
      {CONFETTI.map((c, i) => (
        <span
          key={i}
          className="absolute w-2 h-3 rounded-sm"
          style={{ top: c.top, left: c.left, backgroundColor: c.color, transform: `rotate(${c.rot}deg)` }}
        />
      ))}
      <div className="flex items-center gap-2 relative z-10">
        <Trophy size={16} className="text-[#C6FF3D]" />
        <span className="text-[#C6FF3D] text-xs font-semibold tracking-wide">NEW PR!</span>
      </div>
      <p className="text-white font-display text-2xl mt-1 relative z-10">Bench Press 100 kg</p>
      <span className="inline-block mt-2 bg-[#C6FF3D]/10 text-[#C6FF3D] text-[10px] font-semibold px-2 py-0.5 rounded-full relative z-10">
        +5kg vs last time
      </span>
    </div>
    <div className="space-y-3">
      {[
        { name: "Rohan Mehta", pr: "Squat 120 kg", likes: 24, comments: 5 },
        { name: "Sneha Kulkarni", pr: "Deadlift 95 kg", likes: 18, comments: 3 },
      ].map((post) => (
        <div key={post.name} className="bg-[#0A0A0B] border border-[#232326] rounded-xl p-3.5">
          <p className="text-white text-xs font-semibold">{post.name}</p>
          <p className="text-[#A1A1AA] text-xs mb-2">hit a new PR: {post.pr} 🎉</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 text-[#A1A1AA] text-xs">
              <span className="flex items-center gap-1">
                <Heart size={12} className="text-[#C6FF3D]" fill="#C6FF3D" /> {post.likes}
              </span>
              <span className="flex items-center gap-1">
                <MessageCircle size={12} /> {post.comments}
              </span>
            </div>
            <span className="border border-[#C6FF3D]/40 text-[#C6FF3D] text-[10px] font-semibold px-2.5 py-1 rounded-full">
              Congratulate
            </span>
          </div>
        </div>
      ))}
    </div>
  </MockCard>
);

const BEFORE_IMG =
  "https://images.unsplash.com/photo-1632482151451-ccf4baf7f6a3?crop=entropy&cs=srgb&fm=jpg&q=85";
const AFTER_IMG =
  "https://images.unsplash.com/photo-1682241367368-6387d5d4921a?crop=entropy&cs=srgb&fm=jpg&q=85";

const CheckinMock = () => {
  const [slider, setSlider] = useState(50);
  return (
    <MockCard>
      <div className="relative h-56 sm:h-64 rounded-2xl overflow-hidden mb-5">
        <img src={BEFORE_IMG} alt="Before physique check-in" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - slider}% 0 0)` }}>
          <img src={AFTER_IMG} alt="After physique check-in" className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-[#C6FF3D]"
          style={{ left: `${slider}%` }}
        />
        <span className="absolute top-3 left-3 bg-black/60 text-white text-[10px] font-semibold px-2 py-1 rounded-full">Week 1</span>
        <span className="absolute top-3 right-3 bg-black/60 text-[#C6FF3D] text-[10px] font-semibold px-2 py-1 rounded-full">Week 8</span>
        <input
          type="range"
          min="0"
          max="100"
          value={slider}
          onChange={(e) => setSlider(Number(e.target.value))}
          data-testid="checkin-slider"
          aria-label="Compare before and after photos"
          className="absolute bottom-3 left-3 right-3 w-[calc(100%-24px)] accent-[#C6FF3D]"
        />
      </div>
      <div className="bg-[#0A0A0B] border border-[#232326] rounded-2xl p-4 mb-4">
        <span className="text-[#C6FF3D] text-xs font-semibold tracking-wide">PROGRESS SEEN</span>
        <p className="text-white text-sm mt-1 mb-3">Leaner waist, fuller shoulders</p>
        <span className="text-[#C6FF3D] text-xs font-semibold tracking-wide">3 SUGGESTIONS</span>
        <ul className="mt-2 space-y-1.5">
          {["Add 2 ab sessions/week", "Increase protein by 20g/day", "Add incline bench for upper chest"].map((s) => (
            <li key={s} className="flex items-center gap-2 text-[#A1A1AA] text-xs">
              <Check size={12} className="text-[#C6FF3D]" /> {s}
            </li>
          ))}
        </ul>
      </div>
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-[#A1A1AA] text-xs">
          <Lock size={12} /> Private — only you and your coach can see your photos.
        </span>
        <span className="bg-[#C6FF3D] text-black text-xs font-semibold px-3 py-1.5 rounded-full flex-shrink-0 ml-2">
          Send to Coach
        </span>
      </div>
    </MockCard>
  );
};

const HEATMAP_DATA = [
  { name: "Bench Press", muscles: ["chest", "triceps", "front-deltoids"] },
  { name: "Squat", muscles: ["quadriceps", "gluteal"] },
  { name: "Pull-ups", muscles: ["upper-back", "biceps"] },
];

const ReportMock = () => (
  <MockCard>
    <div className="flex items-center justify-between mb-5">
      <div>
        <span className="text-[#C6FF3D] text-xs font-semibold tracking-wide">MONTHLY PROGRESS REPORT</span>
        <p className="text-white font-display text-lg">October 2026</p>
      </div>
      <FileText className="text-[#C6FF3D]" size={22} />
    </div>
    <div className="flex items-center justify-center bg-[#0A0A0B] border border-[#232326] rounded-2xl py-3 mb-4">
      <div style={{ width: 110 }}>
        <Model
          data={HEATMAP_DATA}
          type="anterior"
          bodyColor="#1F1F22"
          highlightedColors={["#4a5a24", "#86a83a", "#C6FF3D"]}
          style={{ width: "100%" }}
        />
      </div>
    </div>
    <div className="space-y-2 mb-4">
      {[
        ["Bench Press", "80kg → 90kg", "+12%"],
        ["Squat", "100kg → 115kg", "+15%"],
        ["Deadlift", "110kg → 120kg", "+9%"],
      ].map(([ex, change, pct]) => (
        <div key={ex} className="flex items-center justify-between bg-[#0A0A0B] border border-[#232326] rounded-xl px-4 py-2 text-xs">
          <span className="text-white">{ex}</span>
          <span className="text-[#A1A1AA]">{change}</span>
          <span className="text-[#C6FF3D] font-semibold">{pct}</span>
        </div>
      ))}
    </div>
    <div className="flex items-center justify-between bg-[#0A0A0B] border border-[#232326] rounded-xl px-4 py-2.5 mb-4">
      <span className="flex items-center gap-1.5 text-white text-xs font-semibold">
        <TrendingUp size={13} className="text-[#C6FF3D]" /> Weight: 78kg → 74kg
      </span>
      <span className="text-[#C6FF3D] text-xs font-semibold">-4kg this month</span>
    </div>
    <p className="text-[#A1A1AA] text-xs italic mb-4">
      "Great consistency this month — your pressing strength is up 12% and you're on track for your fat loss goal."
    </p>
    <div className="flex justify-center gap-1.5">
      {[0, 1, 2].map((i) => (
        <span key={i} className={`w-1.5 h-1.5 rounded-full ${i === 0 ? "bg-[#C6FF3D]" : "bg-[#333336]"}`} />
      ))}
    </div>
  </MockCard>
);

const CoachChatMock = () => (
  <MockCard>
    <div className="space-y-3 mb-5">
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded-full bg-[#C6FF3D]/20 flex items-center justify-center text-[#C6FF3D] text-xs font-bold flex-shrink-0">
          CA
        </div>
        <div className="bg-[#0A0A0B] border border-[#232326] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-white max-w-[80%]">
          Great job on the PR! Let's push legs harder this week 💪
        </div>
      </div>
      <div className="flex justify-end">
        <div className="bg-[#C6FF3D] text-black rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm font-medium max-w-[80%]">
          Thanks coach! Ready for leg day
        </div>
      </div>
    </div>
    <div className="bg-[#0A0A0B] border border-[#232326] rounded-2xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Calendar size={16} className="text-[#C6FF3D]" />
        <span className="text-white text-sm font-semibold">1:1 Call</span>
      </div>
      <p className="text-[#A1A1AA] text-xs mb-3">Thu, 6:00 PM with Coach Arjun</p>
      <span className="flex items-center justify-center gap-2 bg-[#C6FF3D] text-black text-xs font-semibold px-4 py-2.5 rounded-full">
        <Video size={13} /> Join Call
      </span>
    </div>
  </MockCard>
);

const BLOCKS = [
  {
    id: "community",
    tag: "All Plans",
    title: "Hit a PR. The whole community celebrates.",
    desc: "FORGE detects your personal records automatically while you log sets. Share it in one tap and everyone in the community gets notified to congratulate you.",
    Mock: PRCommunityMock,
  },
  {
    id: "checkins",
    tag: "Pro",
    title: "AI Physique Check-ins.",
    desc: "Upload a photo every week. Our AI analyses your progress, flags focus areas and gives you 3 concrete suggestions — then your coach reviews it too.",
    Mock: CheckinMock,
  },
  {
    id: "reports",
    tag: "Pro",
    title: "Your progress, in one report.",
    desc: "Get a weekly and monthly report comparing you to your last period — strength gains, weight change, a muscle heatmap and an AI summary. Download it as a PDF anytime.",
    Mock: ReportMock,
  },
  {
    id: "coach-chat",
    tag: "Coaching",
    title: "Your coach, one tap away.",
    desc: "Chat with Coach Arjun directly, book two 1:1 video calls a month, and let him personally fine-tune your plan whenever you need it.",
    Mock: CoachChatMock,
  },
];

export const WhatsNew = () => (
  <section id="whats-new" className="py-14 md:py-20 px-6 bg-[#111113]">
    <div className="max-w-6xl mx-auto">
      <FadeUp>
        <h2 className="font-display text-4xl sm:text-5xl text-white text-center mb-14 md:mb-20">
          WHAT'S NEW IN FORGE
        </h2>
      </FadeUp>

      <div className="space-y-16 md:space-y-24">
        {BLOCKS.map((b, i) => (
          <div
            key={b.id}
            data-testid={`whats-new-${b.id}`}
            className="grid md:grid-cols-2 gap-10 md:gap-14 items-center"
          >
            <FadeUp className={i % 2 === 1 ? "md:order-2" : ""}>
              <Tag>{b.tag}</Tag>
              <h3 className="font-display text-2xl sm:text-3xl text-white mb-4 leading-tight">{b.title}</h3>
              <p className="text-[#A1A1AA] text-sm sm:text-base max-w-md">{b.desc}</p>
            </FadeUp>
            <FadeUp delay={0.1} className={i % 2 === 1 ? "md:order-1" : ""}>
              <b.Mock />
            </FadeUp>
          </div>
        ))}
      </div>
    </div>
  </section>
);
